<script setup lang="ts">
import client from "@/utils/axios";
import { errorToast, successToast } from "@/utils/custom-toast";
import { defineEmits, ref, defineProps } from "vue";
import { useTranslate } from "@/composables/useTranslate";

type Emits = {
  (event: "close"): void;
  (event: "refresh"): void;
};

type Props = {
  termin: any;
  isSignedDocument: boolean;
};

const props = defineProps<Props>();
const emits = defineEmits<Emits>();
const { t } = useTranslate();

const radioButtons = ref([
  {
    id: "1",
    name: "radio1",
    text: "Vollabnahme",
  },
  {
    id: "2",
    name: "radio2",
    text: "Teilabnahme",
  },
]);

const radioValue = ref<string | null>(null);
const checkBoxValue = ref(props.isSignedDocument);
const uploadedFile = ref();
const isLoading = ref(false);

const onUpdateCheckboxValue = () => {
  checkBoxValue.value = !checkBoxValue.value;
};

const onUpdateRadioValue = (value: string) => {
  radioValue.value = value;
};

const handleFileChange = (event: any) => {
  if (event.target.files) {
    uploadedFile.value = Array.from(event.target.files)[0];
  }
};

const getFileType = (fileName: string) => {
  const match = fileName.match(/\.([0-9a-z]+)$/i);
  return match ? match[1].toLowerCase() : "";
};

const onSubmit = async () => {
  isLoading.value = true;

  if (uploadedFile.value && radioValue.value) {
    let fileType = getFileType(uploadedFile.value.name);
    const formData = new FormData();
    formData.append("question", radioValue.value);
    formData.append("isDocumentSigned", checkBoxValue.value + "");
    formData.append("extension", fileType);

    if (uploadedFile.value) {
      formData.append(`files[${0}]`, uploadedFile.value);
    }

    if (props.isSignedDocument) {
      await client.post(
        `/termin/${props.termin}/abnahmeprotokoll/signed-upload`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
    } else {
      await client.post(
        `/termin/${props.termin}/abnahmeprotokoll/manual-upload`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
    }

    isLoading.value = false;
    successToast("Successfully uploaded abnahmeprotokoll.");
    emits("refresh");
    emits("close");
  } else {
    isLoading.value = false;
    errorToast("Acceptance status and file are required.");
  }
};
</script>

<template>
  <div
    class="fixed inset-0 bg-black bg-opacity-80 flex justify-center items-center z-50 transition-opacity duration-300 ease-in-out p-4"
  >
    <div
      class="flex flex-col max-w-[628px] w-full h-fit p-6 mx-auto rounded-primary-radius bg-[#FFFFFF]"
    >
      <div
        class="flex items-center justify-between pb-[16px] border-b border-[#E3E1EA]"
      >
        <!-- left side -->
        <div class="flex items-center justify-start gap-2">
          <h3
            class="text-primary-text-color text-xl leading-[32px] font-semibold tracking-[0.01rem]"
          >
            {{ t("phase.upload_abnahmeprotokoll") }}
          </h3>
        </div>
        <!-- close button -->
        <img
          @click="emits('close')"
          class="cursor-pointer"
          src="@/assets/close-icon-2.svg"
          alt="Close icon"
        />
      </div>
      <!-- Content -->
      <div>
        <!-- Image upload -->

        <div class="w-full h-[134px] flex items-center justify-center">
          <label
            v-if="!uploadedFile"
            for="file"
            class="w-full h-full mt-5 flex flex-col items-center justify-center border-dashed border-2 border-[#A7A5AE] rounded-primary-radius cursor-pointer"
          >
            <img
              class="w-7 h-7 object-cover"
              src="@/assets/file-upload-icon.svg"
              alt="Attach icon"
            />
            <h4
              class="text-base leading-[26px] tracking-[0.01rem] text-[#898790] font-medium pt-1"
            >
              {{ t("phase.choose_file") }}
            </h4>
          </label>
          <label
            v-else
            for="file"
            class="w-full h-full mt-5 flex flex-col items-center justify-center border-dashed border-2 border-[#393840] rounded-primary-radius cursor-pointer bg-[#F9F8F6]"
          >
            <img
              class="w-7 h-7 object-cover"
              src="@/assets/paperclip-attachment.svg"
              alt="Attach icon"
            />
            <h4
              class="text-base leading-[26px] tracking-[0.01rem] text-[#898790] font-medium pt-1"
            >
              {{ uploadedFile.name }}
            </h4>
          </label>

          <input
            class="hidden"
            type="file"
            id="file"
            @change="handleFileChange"
          />
        </div>

        <!-- Radio buttons -->
        <div class="mt-[24px]">
          <h3
            class="text-base leading-[26px] tracking-[0.01rem] font-medium text-[#898790]"
          >
            {{ t("phase.acceptance_status") }}
          </h3>
          <div class="flex items-center gap-2 mt-2">
            <div v-for="radio in radioButtons" :key="radio.id">
              <div
                class="custom-radio-button flex items-center rounded-full py-[2px] px-2 border-2 cursor-pointer"
                :class="
                  radioValue === radio.text
                    ? 'bg-[#DDDDFB] border-transparent border-none'
                    : 'bg-transparent border-primary-text-color'
                "
              >
                <input
                  @change="onUpdateRadioValue(radio.text)"
                  type="radio"
                  :checked="radioValue === radio.text"
                />
                <img
                  v-show="radioValue === radio.text"
                  src="@/assets/double-circle-radio-icon.svg"
                  alt="Circle radio"
                />
                <img
                  v-show="radioValue !== radio.text"
                  src="@/assets/circle-radio-icon.svg"
                  alt=""
                />
                <p class="pl-2 text-sm font-normal text-primary-text-color">
                  {{ radio.text }}
                </p>
              </div>
            </div>
          </div>
        </div>
        <!-- Checkbox -->
        <div class="mt-[24px]">
          <div class="flex items-center gap-2">
            <input
              :disabled="isSignedDocument"
              @change="onUpdateCheckboxValue"
              type="checkbox"
              :checked="checkBoxValue"
              :class="checkBoxValue ? 'hidden' : 'block'"
              id="updateAbnahmeProtocolCheckbox"
            />
            <img
              v-show="checkBoxValue"
              src="@/assets/checkbox-checked.svg"
              :class="`${
                isSignedDocument ? 'cursor-not-allowed' : 'cursor-pointer'
              }`"
              alt=""
            />
            <label
              class="text-base leading-[26px] tracking-[0.01rem] font-medium text-[#898790]"
              :class="`${
                isSignedDocument ? 'cursor-not-allowed' : 'cursor-pointer'
              }`"
              for="updateAbnahmeProtocolCheckbox"
              >{{ t("phase.signed_by_customer") }}</label
            >
          </div>
        </div>
        <!-- Upload button -->
        <div
          class="w-full mt-4 pt-3 border-t border-black/15 flex items-start justify-end"
        >
          <button
            @click="onSubmit"
            class="w-[150px] flex justify-center text-base leading-[20px] tracking-[0.01rem] text-white font-semibold py-[13px] rounded-primary-radius bg-black disabled:bg-[#393840]/40 disabled:cursor-not-allowed"
          >
            <div
              v-if="isLoading"
              class="w-6 h-6 border-4 border-white border-t-transparent rounded-full animate-spin"
            ></div>
            <span v-else>{{ t("global.upload") }}</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
